import React, { useState, useMemo, JSX } from "react";
import {
  applyHeaderFilters,
  getDistinctFieldValues,
  HeaderFilterCriteria,
} from "../Utility/Utility";

export interface ListItem {
  id: string | number;
  [key: string]: any;
}

export interface ColumnDefinition {
  key: string;
  name: string;
  fieldName: string;
  minWidth?: number;
  maxWidth?: number;
  isResizable?: boolean;
  isSortable?: boolean;
  hasHeaderMenu?: boolean;
  headerClassName: string;
  className: string;
  onRender?: (item?: ListItem, index?: number) => JSX.Element | string;
}

export interface DetailedListProps {
  items: any[];
  columns: any;
  title?: string;
  selectionMode?: "none" | "single" | "multiple";
  onItemSelected?: (item: ListItem) => void;
  onSelectionChanged?: (selectedItems: ListItem[]) => void;
  onItemInvoked?: (item: ListItem) => void;
  emptyMessage?: string;
  loading?: boolean;
  className?: string;
  styles?: {
    root?: React.CSSProperties;
  };
  listType?: "shimmered" | "grouped"; // NEW
  groups?: any;
  onColumnHeaderClick?: (
    ev?: React.MouseEvent<HTMLElement>,
    column?: any,
  ) => void;
  enableHeaderMenu?: boolean;
}

const DetailedList: React.FC<DetailedListProps> = ({
  items,
  columns,
  selectionMode = "none",
  onItemSelected,
  onSelectionChanged,
  onItemInvoked,
  emptyMessage = "No items to display",
  loading = false,
  className,
  styles,
  listType = "shimmered", // NEW
  groups,
  onColumnHeaderClick,
  enableHeaderMenu = true,
}) => {
  const [sortedColumnKey, setSortedColumnKey] = useState<string>("");
  const [isSortedDescending, setIsSortedDescending] = useState<boolean>(false);
  const [groupSortState, setGroupSortState] = useState<
    Record<string, { columnKey: string; isDescending: boolean }>
  >({});
  const [headerFilters, setHeaderFilters] = useState<HeaderFilterCriteria>({});
  const [activeHeaderColumn, setActiveHeaderColumn] = useState<any | null>(
    null,
  );
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false);
  const [isFilterSectionOpen, setIsFilterSectionOpen] = useState(false);
  const [headerMenuPosition, setHeaderMenuPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const [selectedItems, setSelectedItems] = useState<ListItem[]>([]);

  const menuButtonStyle: React.CSSProperties = {
    width: "100%",
    textAlign: "left",
    fontSize: 14,
    border: "none",
    background: `hsl(var(--background))`,
    cursor: "pointer",
    borderRadius: 6,
    color: `hsl(var(--foreground))`,
    transition: "background 0.15s ease",
  };

  const filteredItems = useMemo(
    () => (enableHeaderMenu ? applyHeaderFilters(items, headerFilters) : items),
    [items, headerFilters, enableHeaderMenu],
  );

  const sortedItems = useMemo(() => {
    if (!sortedColumnKey) return filteredItems;

    const column = columns.find((col: any) => col.key === sortedColumnKey);
    if (!column || !column.isSortable) return filteredItems;

    return [...filteredItems].sort((a, b) => {
      const aValue = a[column.fieldName];
      const bValue = b[column.fieldName];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return isSortedDescending ? comparison * -1 : comparison;
    });
  }, [filteredItems, sortedColumnKey, isSortedDescending, columns]);

  // Get sorted items for a specific group
  const getSortedGroupItems = (group: any): any[] => {
    const groupItems = filteredItems.slice(
      group.startIndex ?? 0,
      (group.startIndex ?? 0) + (group.count ?? 0),
    );

    const groupSort = groupSortState[group.key];
    if (!groupSort?.columnKey) return groupItems;

    const column = columns.find((col: any) => col.key === groupSort.columnKey);
    if (!column || !column.isSortable) return groupItems;

    return [...groupItems].sort((a, b) => {
      const aValue = a[column.fieldName];
      const bValue = b[column.fieldName];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return groupSort.isDescending ? comparison * -1 : comparison;
    });
  };

  const sortedGroups = useMemo(() => {
    if (!groups || !Array.isArray(groups)) return groups;

    // First, organize sorted items by their group keys
    const groupedItemsMap = new Map<string, any[]>();

    groups.forEach((group) => {
      const groupItems = sortedItems.slice(
        group.startIndex ?? 0,
        (group.startIndex ?? 0) + (group.count ?? 0),
      );
      groupedItemsMap.set(group.key, groupItems);
    });

    // Sort groups alphabetically and recalculate indices
    const alphabeticallySorted = [...groups].sort((a, b) => {
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });

    let currentIndex = 0;
    return alphabeticallySorted.map((group) => {
      const groupItems = groupedItemsMap.get(group.key) || [];
      const newGroup = {
        ...group,
        startIndex: currentIndex,
        count: groupItems.length,
      };
      currentIndex += groupItems.length;
      return newGroup;
    });
  }, [groups, sortedItems]);

  const handleSortAscending = (column?: any) => {
    if (!column) return;
    const columnDef = columns.find((col: any) => col.key === column.key);
    if (!columnDef?.isSortable) return;

    setSortedColumnKey(column.key);
    setIsSortedDescending(false);
  };

  const handleSortDescending = (column?: any) => {
    if (!column) return;
    const columnDef = columns.find((col: any) => col.key === column.key);
    if (!columnDef?.isSortable) return;

    setSortedColumnKey(column.key);
    setIsSortedDescending(true);
  };

  const handleHeaderClick = (
    ev?: React.MouseEvent<HTMLElement>,
    column?: any,
    groupKey?: string,
  ): void => {
    if (onColumnHeaderClick) {
      onColumnHeaderClick(ev, column);
    }

    if (!column) {
      return;
    }

    const columnDef = columns.find((col: any) => col.key === column.key);

    if (columnDef?.isSortable) {
      if (groupKey) {
        // Group-specific sorting
        const currentState = groupSortState[groupKey];
        if (currentState?.columnKey === column.key) {
          setGroupSortState((prev) => ({
            ...prev,
            [groupKey]: {
              columnKey: column.key,
              isDescending: !currentState.isDescending,
            },
          }));
        } else {
          setGroupSortState((prev) => ({
            ...prev,
            [groupKey]: { columnKey: column.key, isDescending: false },
          }));
        }
      } else {
        // Global sorting (non-grouped mode)
        if (sortedColumnKey === column.key) {
          setIsSortedDescending((previous) => !previous);
        } else {
          setSortedColumnKey(column.key);
          setIsSortedDescending(false);
        }
      }
    }

    if (
      !enableHeaderMenu ||
      !ev ||
      !column.fieldName ||
      !columnDef?.hasHeaderMenu
    ) {
      return;
    }

    if (activeHeaderColumn && activeHeaderColumn.key === column.key) {
      setIsHeaderMenuOpen((previous) => !previous);
    } else {
      setActiveHeaderColumn(column);
      setIsHeaderMenuOpen(true);
      setIsFilterSectionOpen(false);
    }

    const target = ev.currentTarget as HTMLElement | null;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    setHeaderMenuPosition({ left: rect.left, top: rect.bottom });
  };

  const activeColumnOptions = useMemo(() => {
    if (!activeHeaderColumn || !activeHeaderColumn.fieldName) {
      return [] as string[];
    }

    return getDistinctFieldValues(
      items,
      activeHeaderColumn.fieldName as keyof any,
    );
  }, [items, activeHeaderColumn]);

  const toggleFilterValue = (value: string, checked: boolean) => {
    if (!activeHeaderColumn || !activeHeaderColumn.fieldName) {
      return;
    }

    const fieldName = activeHeaderColumn.fieldName;

    setHeaderFilters((previous) => {
      const existing = previous[fieldName];
      const current = Array.isArray(existing) ? (existing as string[]) : [];

      if (checked) {
        if (current.includes(value)) {
          return previous;
        }
        return { ...previous, [fieldName]: [...current, value] };
      }

      const updated = current.filter((entry) => entry !== value);
      return { ...previous, [fieldName]: updated };
    });
  };

  const clearActiveColumnFilter = () => {
    if (!activeHeaderColumn || !activeHeaderColumn.fieldName) {
      return;
    }

    const fieldName = activeHeaderColumn.fieldName;
    setHeaderFilters((previous) => ({ ...previous, [fieldName]: [] }));
  };

  const handleItemInvoked = (item: ListItem): void => {
    onItemInvoked?.(item);
  };

  const rootStyle: React.CSSProperties = {
    margin: "5px 0",
    width: "100%",
    background: `hsl(var(--background))`,
    color: `hsl(var(--foreground))`,
    ...(styles?.root || {}),
  };

  const selectionModeMap: Record<string, "none" | "single" | "multiple"> = {
    none: "none",
    single: "single",
    multiple: "multiple",
  };

  const handleRowClick = (item: ListItem) => {
    if (selectionModeMap[selectionMode] === "none") {
      onItemSelected?.(item);
      onSelectionChanged?.([]);
      return;
    }

    setSelectedItems((prev) => {
      let next: ListItem[] = [];
      if (selectionModeMap[selectionMode] === "single") {
        next = [item];
      } else {
        const exists = prev.some((x) => x.id === item.id);
        next = exists ? prev.filter((x) => x.id !== item.id) : [...prev, item];
      }
      onItemSelected?.(item);
      onSelectionChanged?.(next);
      return next;
    });
  };

  const renderTableBody = (rowItems: ListItem[]) => (
    <tbody>
      {rowItems.map((item, index) => {
        const isSelected = selectedItems.some((x) => x.id === item.id);
        return (
          <tr
            key={item.id ?? index}
            className={
              isSelected
                ? "detailed-list-row detailed-list-row--selected"
                : "detailed-list-row"
            }
            onClick={() => handleRowClick(item)}
            onDoubleClick={() => handleItemInvoked(item)}
          >
            {columns.map((col: any) => (
              <td key={col.key} className={col.className}>
                {col.onRender
                  ? col.onRender(item, index)
                  : (item[col.fieldName] as React.ReactNode)}
              </td>
            ))}
          </tr>
        );
      })}
    </tbody>
  );

  const renderHeader = (groupKey?: string) => (
    <thead>
      <tr>
        {columns.map((col: any) => {
          const currentSortState = groupKey
            ? groupSortState[groupKey]
            : { columnKey: sortedColumnKey, isDescending: isSortedDescending };

          const isSorted = currentSortState?.columnKey === col.key;
          const sortIndicator = isSorted
            ? currentSortState?.isDescending
              ? "▼"
              : "▲"
            : "";
          return (
            <th
              key={col.key}
              className={col.headerClassName}
              onClick={(ev) =>
                handleHeaderClick(
                  ev as React.MouseEvent<HTMLElement>,
                  col,
                  groupKey,
                )
              }
              style={{
                cursor:
                  col.isSortable || col.hasHeaderMenu ? "pointer" : "default",
              }}
            >
              <span>{col.name}</span>
              {sortIndicator && (
                <span style={{ marginLeft: 4 }}>{sortIndicator}</span>
              )}
            </th>
          );
        })}
      </tr>
    </thead>
  );

  const renderGroupedContent = () => {
    if (!sortedGroups || !Array.isArray(sortedGroups)) {
      return null;
    }

    return (
      <div className="detailed-list-grouped">
        {sortedGroups.map((group: any) => {
          const groupItems = getSortedGroupItems(group);
          return (
            <div key={group.key} className="detailed-list-group">
              <div className="detailed-list-group-header">
                {group.name}{" "}
                <span style={{ fontSize: "0.85em", opacity: 0.7 }}>
                  ({group.count ?? 0})
                </span>
              </div>
              <div className="detailed-list-table-wrapper">
                <table className="detailed-list-table">
                  {renderHeader(group.key)}
                  {renderTableBody(groupItems)}
                </table>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={className} style={rootStyle}>
      {listType === "shimmered" && (
        <div className="detailed-list-table-wrapper">
          <table className="detailed-list-table">
            {renderHeader()}
            {loading ? (
              <tbody>
                {Array.from({ length: 5 }).map((_, idx) => (
                  <tr
                    key={idx}
                    className="detailed-list-row detailed-list-row--shimmer"
                  >
                    {columns.map((col: any) => (
                      <td key={col.key}>
                        <div className="detailed-list-cell-shimmer" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ) : (
              renderTableBody(sortedItems)
            )}
          </table>
        </div>
      )}

      {listType === "grouped" && renderGroupedContent()}
      {enableHeaderMenu &&
        isHeaderMenuOpen &&
        headerMenuPosition &&
        activeHeaderColumn && (
          <>
            {/* Blur Backdrop */}
            <div
              onClick={() => setIsHeaderMenuOpen(false)}
              style={{
                position: "fixed",
                inset: 0,
                backdropFilter: "blur(1px)",
                background: "rgba(0,0,0,0.15)",
                zIndex: 999,
              }}
            />

            {/* Header Menu */}
            <div
              style={{
                position: "fixed",
                left: headerMenuPosition.left,
                top: headerMenuPosition.top + 6,
                zIndex: 1000,
                background: `hsl(var(--background))`,
                color: `hsl(var(--foreground))`,
                borderRadius: 10,
                border: "2px solid hsl(var(--border))",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                padding: 14,
                fontFamily:
                  "Segoe UI, Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
              }}
            >
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 10,
                }}
              >
                {activeHeaderColumn.name || activeHeaderColumn.fieldName}
              </div>

              <button
                type="button"
                onClick={() => handleSortAscending(activeHeaderColumn)}
                style={menuButtonStyle}
              >
                ↑ Sort A → Z
              </button>

              <button
                type="button"
                onClick={() => handleSortDescending(activeHeaderColumn)}
                style={menuButtonStyle}
              >
                ↓ Sort Z → A
              </button>

              <div
                style={{
                  borderTop: "1px solid #f0f0f0",
                  margin: "10px 0",
                }}
              />

              <button
                type="button"
                onClick={() => setIsFilterSectionOpen((prev) => !prev)}
                style={{
                  ...menuButtonStyle,
                  fontWeight: 600,
                }}
              >
                Filter By
              </button>

              {isFilterSectionOpen && (
                <div
                  style={{
                    maxHeight: 220,
                    overflowY: "auto",
                    paddingTop: 8,
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  {activeColumnOptions.map((value) => (
                    <label
                      key={value}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        fontSize: 13,
                        padding: "4px 4px",
                        borderRadius: 6,
                        cursor: "pointer",
                        color: "hsl(var(--foreground))",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          Array.isArray(
                            headerFilters[
                              activeHeaderColumn.fieldName as string
                            ],
                          )
                            ? (
                                headerFilters[
                                  activeHeaderColumn.fieldName as string
                                ] as string[]
                              ).includes(value)
                            : false
                        }
                        onChange={(event) =>
                          toggleFilterValue(value, event.target.checked)
                        }
                        style={{
                          marginRight: 8,
                          accentColor: "hsl(var(--primary))",
                          cursor: "pointer",
                        }}
                      />
                      <span>{value}</span>
                    </label>
                  ))}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 8,
                      marginTop: 10,
                    }}
                  >
                    <button
                      type="button"
                      onClick={clearActiveColumnFilter}
                      style={{
                        fontSize: 12,
                        padding: "5px 12px",
                        borderRadius: 6,
                        border: "1px solid #d1d1d1",
                        background: "#fafafa",
                        cursor: "pointer",
                        transition: "all 0.15s ease",
                      }}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      {items.length === 0 && !loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 40,
          }}
        >
          <span>{emptyMessage}</span>
        </div>
      )}
    </div>
  );
};

export default DetailedList;
